<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <TableAlert v-if="items.length" />

        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t('Common.costCenters') }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex
              v-if="
                claims.company['ROLE_DELETE_COMPANY_ID_COSTCENTERS'] ||
                claims.costCenter['ROLE_PUT_COSTCENTER_ID'] ||
                claims.costCenter['ROLE_PUT_COSTCENTER_ID_ADDTOALLEMPLOYEES'] ||
                claims.costCenter['ROLE_DELETE_COSTCENTER_ID']
              "
              class="table__checkbox"
              xs1
            >
              <v-checkbox
                v-if="
                  claims.company['ROLE_DELETE_COMPANY_ID_COSTCENTERS'] &&
                  items && items.length
                "
                v-model="checkedAllItems"
                :disabled="!selectedCompany"
                hide-details
                @change="handleCheckedAllItems"
              />
            </v-flex>
            <v-flex xs1>ID</v-flex>
            <v-flex xs3>{{ $t('Common.costCenter') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs2>{{ $t('Common.ownerName') }}</v-flex>
            <v-flex xs2>{{ $t('Common.telephone')}}</v-flex>
            <v-flex xs3>{{ $t('Common.ownerEmail')}}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex
                        v-if="
                          claims.company['ROLE_DELETE_COMPANY_ID_COSTCENTERS'] ||
                          claims.costCenter['ROLE_PUT_COSTCENTER_ID'] ||
                          claims.costCenter['ROLE_PUT_COSTCENTER_ID_ADDTOALLEMPLOYEES'] ||
                          claims.costCenter['ROLE_DELETE_COSTCENTER_ID']
                        "
                        class="table__checkbox table__td--prevent"
                        xs1
                      >
                        <v-checkbox
                          v-if="claims.company['ROLE_DELETE_COMPANY_ID_COSTCENTERS']"
                          v-model="selectedItems"
                          :value="item.id"
                          :disabled="!selectedCompany"
                          hide-details
                          @change="handleSelectedItems"
                        />

                        <v-menu
                          v-if="
                            claims.costCenter['ROLE_PUT_COSTCENTER_ID'] ||
                            claims.costCenter['ROLE_PUT_COSTCENTER_ID_ADDTOALLEMPLOYEES'] ||
                            claims.costCenter['ROLE_DELETE_COSTCENTER_ID']
                          "
                          offset-y
                          right
                        >
                          <button class="table-body__actions" slot="activator">
                            <v-icon>
                              more_vertical
                            </v-icon>
                          </button>
                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile
                              v-if="claims.costCenter['ROLE_PUT_COSTCENTER_ID']"
                              :to="{ name: 'CostCenter', params: { type: 'editar', id: item.id } }"
                            >
                              {{ $t('Common.edit') }} {{ $t('Common.costCenter').toLowerCase() }}
                            </v-list-tile>

                            <v-list-tile
                              v-if="claims.costCenter['ROLE_PUT_COSTCENTER_ID'] && !item.temporaryDisabled"
                              @click="handleEnabledCostCenterMonth({
                                costCenter: item,
                                temporaryDisabled: true
                              })"
                            >
                              {{ $t('Components.CostCenterForm.disableCostCenterByTheEndOfTheMonth') }}
                            </v-list-tile>

                            <v-list-tile
                              v-if="claims.costCenter['ROLE_PUT_COSTCENTER_ID'] && item.temporaryDisabled"
                              @click="handleEnabledCostCenterMonth({
                                costCenter: item,
                                temporaryDisabled: false
                              })"
                            >
                              {{ $t('Components.CostCenterForm.enabledCostCenter') }}
                            </v-list-tile>

                            <v-list-tile
                              v-if="claims.costCenter['ROLE_PUT_COSTCENTER_ID_ADDTOALLEMPLOYEES']"
                              @click="handleAddCostCenterToAllEmployees(item)"
                            >
                              {{ $t('Components.CostCenterForm.addCostCenterToAllEmployees') }}
                            </v-list-tile>

                            <v-divider
                              v-if="(
                                claims.costCenter['ROLE_PUT_COSTCENTER_ID'] ||
                                claims.costCenter['ROLE_PUT_COSTCENTER_ID_ADDTOALLEMPLOYEES']
                              ) &&
                              claims.costCenter['ROLE_DELETE_COSTCENTER_ID']
                              "
                            />

                            <v-list-tile
                              v-if="claims.costCenter['ROLE_DELETE_COSTCENTER_ID']"
                              @click="handleSingleDeleteDialog(item)"
                            >
                              {{ $t('Common.delete') }} {{ $t('Common.costCenter').toLowerCase() }}
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.id" class="text--ellipsis" contenteditable>
                          {{ item.id }}
                        </span>
                      </v-flex>

                      <v-flex xs3>
                        <span class="text--ellipsis" :title="item.name">
                          {{ item.name }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span  v-if="isValidArray(item.owners)" class="text--ellipsis">
                          <span v-for="(owner, index) in checkOwnersList(item.owners)" :key="index" :title="owner.name">
                            {{ owner.name }}<span v-if="checkOwnersList(item.owners).length !== index + 1">, </span>
                          </span>
                          <span v-if="item.owners.length > 2">...</span>
                        </span>

                        <span v-else-if="!claims.costCenter['ROLE_PUT_COSTCENTER_ID']">-</span>

                        <router-link
                          v-else
                          :to="{ name: 'CostCenter', params: { type: 'editar', id: item.id } }"
                          :title="$t('Common.setOwner')"
                          class="accent--text"
                        >
                          {{ $t('Common.setOwner') }}
                        </router-link>
                      </v-flex>

                      <v-flex xs2>
                        <span  v-if="isValidArray(item.owners)" class="text--ellipsis">
                          <span v-for="(owner, index) in checkOwnersList(item.owners)" :key="index" :title="owner.phone">
                            {{ owner.phone || '-' }}<span v-if="checkOwnersList(item.owners).length !== index + 1">, </span>
                          </span>
                          <span v-if="item.owners.length > 2">...</span>
                        </span>

                        <span v-else-if="!claims.costCenter['ROLE_PUT_COSTCENTER_ID']">-</span>

                        <router-link
                          v-else
                          :to="{ name: 'CostCenter', params: { type: 'editar', id: item.id } }"
                          :title="$t('Common.setOwner')"
                          class="accent--text"
                        >
                          {{ $t('Common.setOwner') }}
                        </router-link>
                      </v-flex>

                      <v-flex xs3>
                        <span  v-if="isValidArray(item.owners)" class="text--ellipsis">
                          <span v-for="(owner, index) in checkOwnersList(item.owners)" :key="index" :title="owner.email">
                            {{ owner.email }}<span v-if="checkOwnersList(item.owners).length !== index + 1">, </span>
                          </span>
                          <span v-if="item.owners.length > 2">...</span>
                        </span>

                        <span v-else-if="!claims.costCenter['ROLE_PUT_COSTCENTER_ID']">-</span>

                        <router-link
                          v-else
                          :to="{ name: 'CostCenter', params: { type: 'editar', id: item.id } }"
                          :title="$t('Common.setOwner')"
                          class="accent--text"
                        >
                          {{ $t('Common.setOwner') }}
                        </router-link>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneCostCenterFound`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, formatNumber } from 'utils/functions'
import TableAlert from './TableAlert'
import EmptyState from 'components/EmptyState'
import GlobalDrawer from 'components/globalDrawer'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    clearSelectedItems: {
      type: Boolean
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    TableAlert,
    EmptyState,
    GlobalDrawer
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.CostCenterPage',
      selectedItem: null,
      checkedAllItems: false,
      selectedItems: [],
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    isEmailOnOwners (owners, ownerEmail) {
      return owners.length ? owners.some(owner => owner.email !== ownerEmail) : true
    },

    openGlobalDrawer ({ data = {}, filters = {} }) {
      this.globalDrawer = {
        visibility: true,
        type: 'costCenter',
        data,
        filters: {
          ...filters,
          costCenterId: data.id
        }
      }
    },

    closeGlobalDrawer (reload = null) {
      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }

      this.selectedItem = null
      reload && this.$emit('reloadData')
    },

    handlePage (page) {
      this.closeGlobalDrawer()
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.id === this.selectedItem || this.selectedItems.find(i => i === item.id)
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.id
        this.openGlobalDrawer({
          data: {
            id: item.id,
            name: item.name
          }
        })
      }
    },

    openDrawerIfIdExist () {
      if (this.routeId) {
        this.openGlobalDrawer({
          data: {
            id: this.routeId
          }
        })
        this.selectedItem = this.routeId
      }
    },

    handleSingleDeleteDialog (item) {
      this.$emit('deleteItem', item)
    },

    handleCheckedAllItems (val) {
      this.selectedItems = val
        ? [...this.items].map(item => item.id)
        : []
    },

    handleSelectedItems (val) {
      this.checkedAllItems = this.selectedItems.length === this.items.length
    },

    checkOwnersList (owners) {
      if (!this.isValidArray(owners)) return []
      if (owners.length > 2) return owners.filter((owner, index) => index <= 1)
      return owners
    },

    async handleEnabledCostCenterMonth ({ costCenter, temporaryDisabled }) {
      try {
        const name = costCenter.name ? costCenter.name : '-'
        const obj = { ...costCenter }

        await this.$apiService.updateCostCenter({
          ...obj,
          name,
          temporaryDisabled
        })

        this.showAlert({
          type: 'success',
          message: temporaryDisabled
            ? this.$t(`Components.CostCenterForm.disableCostCenterMonthSuccess`)
            : this.$t(`Components.CostCenterForm.enabledCostCenterMonthSuccess`)
        })

        this.closeGlobalDrawer(true)
      } catch (err) {
        console.log('Error', err)

        if (err.response.data && err.response.data.errors) {
          const key = Object.keys(err.response.data.errors)[0]
          let msg

          if (key === 'name') {
            msg = this.$t(`Components.CostCenterForm.costCenteRequiredMessage`)
            return this.showAlert({ type: 'error', message: msg })
          }

          if (key === 'companyId') {
            msg = this.$t(`Components.CostCenterForm.selectedACompany`)
            return this.showAlert({ type: 'error', message: msg })
          }

          this.showAlert({
            type: 'error',
            message: temporaryDisabled
              ? this.$t(`Components.CostCenterForm.disableCostCenterMonthError`)
              : this.$t(`Components.CostCenterForm.enabledCostCenterMonthError`)
          })
        }

        this.showAlert({
          type: 'error',
          message: temporaryDisabled
            ? this.$t(`Components.CostCenterForm.disableCostCenterMonthError`)
            : this.$t(`Components.CostCenterForm.enabledCostCenterMonthError`)
        })
      }
    },

    async handleAddCostCenterToAllEmployees (costCenter) {
      try {
        await this.$apiService.addToAllEmployees(costCenter.id)
        this.showAlert({
          type: 'success',
          message: this.$t(`${this.namespace}.addToAllEmployeesSuccess`)
        })

        this.closeGlobalDrawer(true)
      } catch (err) {
        console.log('Error', err)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.addToAllEmployeesError`)
        })
      }
    }
  },
  watch: {
    selectedCompany () {
      this.closeGlobalDrawer()
    },

    selectedItems (val) {
      this.$emit('selectedItems', val)
    },

    clearSelectedItems () {
      this.selectedItems = []
      this.checkedAllItems = false
    },

    drawerVisibility () {
      this.closeGlobalDrawer()
    }
  },
  mounted () {
    this.openDrawerIfIdExist()
  }
}
</script>
